import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useSetRecoilState } from 'recoil'
import styled from 'styled-components'

import { usePatchMyPassword } from '~/features/mypage/queries/useMyInfo'
import Close from '~/shared/assets/icons/Close'
import Eye from '~/shared/assets/icons/Eye'
import PasswordValidationCheck from '~/shared/components/modals/auth/PasswordValidationCheck'
import { type ApiResponse } from '~/shared/http-client/exceptions/type'
import { isModalOpenState } from '~/shared/states'
import theme from '~/shared/styles/theme'

type Inputs = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

function ChangePasswordModal() {
  const setIsModalOpen = useSetRecoilState(isModalOpenState)
  const { enqueueSnackbar } = useSnackbar()

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const [isPasswordValidated, setIsPasswordValidated] = useState<boolean>(false)
  const [isPasswordMatched, setIsPasswordMatched] = useState<boolean>(false)

  const { mutate: patchPassword } = usePatchMyPassword()

  const { register, handleSubmit, watch } = useForm<Inputs>()

  const [oldPassword, newPassword, confirmPassword] = watch([
    'oldPassword',
    'newPassword',
    'confirmPassword',
  ])

  const onSubmit: SubmitHandler<Inputs> = () => {
    if (isPasswordValidated && isPasswordMatched) {
      patchPassword(
        {
          userInfo: {
            password: oldPassword,
            new_password: newPassword,
          },
        },
        {
          onSuccess: (res) => {
            if ((res as ApiResponse).errorCode === 0) {
              enqueueSnackbar('비밀번호가 변경되었습니다.', {
                variant: 'success',
              })
              setIsModalOpen({ contents: '', isOpen: false })

              return
            }

            enqueueSnackbar((res as ApiResponse).errorMsg, { variant: 'error' })

            return
          },
        },
      )
    }
  }

  useEffect(() => {
    if (newPassword === confirmPassword) {
      setIsPasswordMatched(true)
    } else {
      setIsPasswordMatched(false)
    }
  }, [newPassword, confirmPassword])

  return (
    <Wrapper onClick={(e) => e.stopPropagation()}>
      <CloseIcon
        onClick={() => setIsModalOpen({ contents: '', isOpen: false })}
      />
      <Title>비밀번호 변경</Title>

      <InputBox onSubmit={handleSubmit(onSubmit)}>
        <div>
          <PasswordLabel>
            <InputTitle>기존 비밀번호 입력</InputTitle>
            <Input
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder="Password"
              {...register('oldPassword')}
            />
            <EyeIcon
              color={
                isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
              }
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            />
          </PasswordLabel>
        </div>
        <div>
          <PasswordLabel>
            <InputTitle>비밀번호</InputTitle>
            <Input
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder="Password"
              {...register('newPassword')}
            />
            <EyeIcon
              color={
                isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
              }
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            />
          </PasswordLabel>
          <PasswordValidationCheck
            passwordInput={newPassword || ''}
            setIsPasswordValidated={setIsPasswordValidated}
          />
        </div>
        <div>
          <PasswordLabel>
            <InputTitle>비밀번호 확인</InputTitle>
            <Input
              type={isPasswordVisible ? 'text' : 'password'}
              className={isPasswordMatched ? '' : 'warning'}
              placeholder="Password"
              onKeyUp={(e) => {
                if (e.key === 'Enter') handleSubmit(onSubmit)
              }}
              {...register('confirmPassword')}
            />
            <EyeIcon
              color={
                isPasswordVisible ? theme.colors.grey_5 : theme.colors.grey_4
              }
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            />
          </PasswordLabel>
          {!isPasswordMatched && (
            <PasswordNotMatchedErrorText>
              비밀번호가 일치하지 않습니다.
            </PasswordNotMatchedErrorText>
          )}
          <ModifyButton type="submit" value="저장하기" />
        </div>
      </InputBox>
    </Wrapper>
  )
}

export default ChangePasswordModal

const Wrapper = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  gap: 36px;
  align-items: center;

  box-sizing: border-box;
  width: 500px;
  height: auto;
  max-height: 80vh;
  margin: 0 auto;
  padding: 108px 77px 60px;

  background-color: ${theme.colors.white};
  border-radius: 12px;

  @media ${theme.device.medium} {
    top: 50%;
    width: 90%;
    height: auto;
    padding: 56px 20px 30px;
  }
`

const InputBox = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const CloseIcon = styled(Close)`
  position: absolute;
  top: 36px;
  right: 36px;

  @media ${theme.device.medium} {
    top: 20px;
    right: 20px;
    width: 24px;
    height: 24px;
  }
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 42px;
  font-weight: 500;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const PasswordLabel = styled.label`
  position: relative;
`

const EyeIcon = styled(Eye)`
  position: absolute;
  top: 85%;
  right: 20px;
  transform: translateY(-50%);
`

const InputTitle = styled.div`
  bottom: 12px;

  margin: 12px 0;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  color: ${theme.colors.grey_4};
`

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 49px;
  padding: 12px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;

  background-color: ${theme.colors.grey_1};
  border: none;
  border-radius: 12px;

  &.warning {
    border: 1px solid ${theme.colors.warning};
  }

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const ModifyButton = styled.input`
  cursor: pointer;

  gap: 4px;

  width: 100%;
  height: 48px;
  margin-top: 24px;
  padding: 8px 20px;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: ${theme.colors.white};

  background-color: ${theme.colors.grey_5};
  border: none;
  border-radius: 50px;

  @media ${theme.device.medium} {
    font-size: 15px;
  }
`

const PasswordNotMatchedErrorText = styled.span`
  margin-top: 8px;
  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 12px;
  color: ${theme.colors.warning};
`
